import React, { useState, useRef } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Carousel from 'components/consumer/Carousel';
import BrowserOnly from 'components/consumer/BrowserOnly';
import PDPView3DButton from 'components/consumer/PDP/PDPView3DButton';
import RatioContainer from 'components/consumer/RatioContainer';
import JBProductImage from 'components/essentials/JBImage/JBProductImage';
import PDPFullScreenGallery from 'components/consumer/PDP/PDPFullScreenGallery';
import PDPMobileGalleryZoomIcon from 'components/consumer/PDP/PDPMobileGalleryZoomIcon';
import PDPFavoriteButton from 'components/consumer/PDPFavoriteButton';
import scrollIntoViewUtil from 'commons/scrollIntoViewUtil';
import { markLuxPageNavigationEnd } from 'commons/lux';
import useRemoveBrokenImageUrls from 'global-state/brokenImageUrls/useRemoveBrokenImageUrls';
import PDPProductIntroVideo from '../PDPDesktopHeroGallery/PDPProductIntroVideo';
import { getMainImageImgixPropsForPhoto } from '../PDPDesktopHeroGallery/helpers/imgix-props';
import PDPShownIn from '../PDPShownIn';

const PDPMobileCarousel = ({
  displayShownIn = false,
  enableCrops = true,
  headerLeft,
  isConfigFlipped = false,
  isEditOrder = false,
  isHero = false,
  maxPhotos = 16,
  onAfterSlide,
  photos = [],
  productName,
  rightButtons,
  showARIcon = false,
  showDecideLaterOverlay = false,
  showFavoriteButton = false,
  videoId,
  zoomOnClick = true,
}) => {
  const validPhotos = useRemoveBrokenImageUrls(photos);

  const view3DButtonRef = useRef(true);

  const isConfigurationGallery =
    validPhotos?.[0]?.type === 'configuration' ||
    validPhotos?.[0]?.type === 'cylindo';

  const [isFullScreenGalleryOpen, setIsFullScreenGalleryOpen] = useState(false);
  const [selectedCarouselIndex, setSelectedCarouselIndex] = useState(0);
  const [brokenImages, setBrokenImages] = useState({});

  const hasMoreThanMaxPhotos = validPhotos.length > maxPhotos;

  let carouselPhotos = hasMoreThanMaxPhotos
    ? validPhotos.slice(0, maxPhotos)
    : validPhotos;
  carouselPhotos =
    videoId && carouselPhotos.length
      ? [carouselPhotos[0], { videoId }, ...carouselPhotos.slice(1)]
      : carouselPhotos;

  const standardRatio = isConfigurationGallery ? 2 / 3 : 1 / 2;
  const scrollDownToCustomizer = () => {
    if (!displayShownIn) {
      return;
    }
    scrollIntoViewUtil('[data-xc="PDPMobileConfigurationPhotoGalleryUI"]', 0);
  };

  return (
    <>
      <div className="relative">
        <Carousel
          selectedDotColor={isConfigurationGallery ? 'brand' : 'white'}
          afterSlide={
            showDecideLaterOverlay
              ? () => {}
              : idx => {
                  setSelectedCarouselIndex(idx);
                  if (onAfterSlide) {
                    onAfterSlide(idx);
                  }
                }
          }
          disableEdgeSwiping={showDecideLaterOverlay}
        >
          {carouselPhotos
            .map(
              (
                {
                  src,
                  id,
                  type,
                  cropData,
                  // eslint-disable-next-line no-shadow
                  videoId,
                  optionId,
                  upholsteryName,
                  valueId,
                },
                index
              ) => (
                <RatioContainer
                  className={classNames({
                    'pt-8 px-6 pb-11': isConfigurationGallery && !enableCrops,
                    'scale-x-[-1]': isConfigurationGallery && isConfigFlipped,
                    'flex justify-center': brokenImages[src],
                  })}
                  ratio={enableCrops ? 1 : standardRatio}
                  key={`${id || ''}-${videoId || ''}`}
                >
                  {index === 1 && videoId ? (
                    <PDPProductIntroVideo videoId={videoId} />
                  ) : (
                    <>
                      {displayShownIn ? (
                        <PDPShownIn
                          isEditOrder={isEditOrder}
                          optionId={optionId}
                          upholsteryName={upholsteryName}
                          valueId={valueId}
                          layout="roundSwatchLabel"
                          onSelection={scrollDownToCustomizer}
                          productName={productName}
                          showInactiveUpholstery
                        />
                      ) : null}
                      <JBProductImage
                        className="h-full object-contain w-full"
                        {...getMainImageImgixPropsForPhoto(
                          { src, type, cropData },
                          enableCrops
                        )}
                        lazy={index !== 0}
                        sizes="100vw"
                        onClick={
                          zoomOnClick
                            ? showDecideLaterOverlay
                              ? () => {}
                              : () => {
                                  setSelectedCarouselIndex(index);
                                  setIsFullScreenGalleryOpen(true);
                                }
                            : undefined
                        }
                        forPage="pdp"
                        onLoad={isFail => {
                          if (index === 0 && isHero) {
                            markLuxPageNavigationEnd();
                          }

                          if (!isFail) {
                            return;
                          }
                          setBrokenImages({ ...brokenImages, [src]: true });
                        }}
                      />
                    </>
                  )}

                  {hasMoreThanMaxPhotos && index === carouselPhotos.length - 1 && (
                    <div className="items-center bottom-0 flex justify-center left-0 pointer-events-none absolute right-0 top-0">
                      <div className="text-lg font-bold bg-brand-lightest text-brand-dark px-4 py-3">
                        Explore {validPhotos.length - carouselPhotos.length}+
                        more photos
                      </div>
                    </div>
                  )}
                </RatioContainer>
              )
            )
            .filter(Boolean)}
        </Carousel>

        <div
          className={classNames('absolute right-4 top-4 z-[1]', {
            'justify-between left-4': !displayShownIn,
            'justify-end': displayShownIn,
            flex: !showDecideLaterOverlay,
            '!hidden': showDecideLaterOverlay,
          })}
        >
          {!displayShownIn ? (
            <div className="pointer-events-none">
              {!!headerLeft && headerLeft}
            </div>
          ) : null}

          <div className="flex [&_>_*_+_*]:ml-2.5">
            {showARIcon && (
              <span ref={view3DButtonRef}>
                <PDPView3DButton />
              </span>
            )}
            {!!zoomOnClick && !(selectedCarouselIndex === 1 && videoId) && (
              <PDPMobileGalleryZoomIcon
                inverted={isConfigurationGallery}
                onClick={() => {
                  setIsFullScreenGalleryOpen(true);
                }}
              />
            )}

            <div className="flex flex-col gap-2">
              {showFavoriteButton && (
                <PDPFavoriteButton size="sm" className="ml-0" />
              )}

              {!!rightButtons && rightButtons}
            </div>
          </div>
        </div>
      </div>

      <BrowserOnly>
        <PDPFullScreenGallery
          isEditOrder={isEditOrder}
          isOpen={isFullScreenGalleryOpen}
          photos={validPhotos}
          initialSelectedPhotoIndex={selectedCarouselIndex}
          onClose={() => {
            setIsFullScreenGalleryOpen(false);
          }}
          productName={productName}
          displayShownIn={displayShownIn}
        />
      </BrowserOnly>
    </>
  );
};

PDPMobileCarousel.propTypes = {
  displayShownIn: PropTypes.bool,
  enableCrops: PropTypes.bool,
  headerLeft: PropTypes.node,
  isConfigFlipped: PropTypes.bool,
  isEditOrder: PropTypes.bool,
  isHero: PropTypes.bool,
  maxPhotos: PropTypes.number,
  onAfterSlide: PropTypes.func,
  onDimensionsIconClick: PropTypes.func,
  photos: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      src: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  productName: PropTypes.string,
  rightButtons: PropTypes.node,
  showARIcon: PropTypes.bool,
  showDecideLaterOverlay: PropTypes.bool,
  showFavoriteButton: PropTypes.bool,
  videoId: PropTypes.string,
  zoomOnClick: PropTypes.bool,
};

export default PDPMobileCarousel;
